import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const VoyageDetailsForm = ({ onVoyageDataChange, voyageData }) => {
  const [formData, setFormData] = useState({
    vesselName: '',
    vesselType: '',
    dwt: '',
    voyageNumber: '',
    departurePort: '',
    arrivalPort: '',
    cargoType: '',
    cargoQuantity: '',
    charterType: '',
    bimcoClause: '',
    estimatedDistance: '',
    estimatedDuration: ''
  });

  const [connectedSystems, setConnectedSystems] = useState({
    veson: { status: 'connected', lastSync: '2 min ago' },
    dataloy: { status: 'connected', lastSync: '5 min ago' },
    danaos: { status: 'warning', lastSync: '15 min ago' }
  });

  const vesselTypes = [
    { value: 'bulk-carrier', label: 'Bulk Carrier' },
    { value: 'container', label: 'Container Ship' },
    { value: 'tanker', label: 'Tanker' },
    { value: 'general-cargo', label: 'General Cargo' },
    { value: 'ro-ro', label: 'RoRo' }
  ];

  const charterTypes = [
    { value: 'voyage', label: 'Voyage Charter' },
    { value: 'time', label: 'Time Charter' },
    { value: 'bareboat', label: 'Bareboat Charter' },
    { value: 'coa', label: 'Contract of Affreightment' }
  ];

  const bimcoClauses = [
    { value: 'ets-2023', label: 'BIMCO ETS Clause 2023' },
    { value: 'ets-2024', label: 'BIMCO ETS Clause 2024' },
    { value: 'custom-ets', label: 'Custom ETS Allocation' },
    { value: 'fueeu-standard', label: 'FuelEU Maritime Standard' }
  ];

  const cargoTypes = [
    { value: 'iron-ore', label: 'Iron Ore' },
    { value: 'coal', label: 'Coal' },
    { value: 'grain', label: 'Grain' },
    { value: 'containers', label: 'Containers' },
    { value: 'crude-oil', label: 'Crude Oil' },
    { value: 'lng', label: 'LNG' },
    { value: 'general', label: 'General Cargo' }
  ];

  const ports = [
    { value: 'rotterdam', label: 'Rotterdam, Netherlands' },
    { value: 'hamburg', label: 'Hamburg, Germany' },
    { value: 'antwerp', label: 'Antwerp, Belgium' },
    { value: 'singapore', label: 'Singapore' },
    { value: 'shanghai', label: 'Shanghai, China' },
    { value: 'los-angeles', label: 'Los Angeles, USA' },
    { value: 'piraeus', label: 'Piraeus, Greece' },
    { value: 'valencia', label: 'Valencia, Spain' }
  ];

  useEffect(() => {
    if (voyageData) {
      setFormData(voyageData);
    }
  }, [voyageData]);

  const handleInputChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onVoyageDataChange(updatedData);
  };

  const handleAutoFetch = () => {
    // Simulate auto-fetch from VMS
    const mockData = {
      vesselName: 'MV Atlantic Voyager',
      vesselType: 'bulk-carrier',
      dwt: '82500',
      voyageNumber: 'AV-2024-089',
      departurePort: 'rotterdam',
      arrivalPort: 'singapore',
      cargoType: 'iron-ore',
      cargoQuantity: '75000',
      estimatedDistance: '11250',
      estimatedDuration: '18'
    };

    setFormData(prev => ({ ...prev, ...mockData }));
    onVoyageDataChange({ ...formData, ...mockData });
  };

  const getSystemStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getSystemStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'XCircle';
      default: return 'Circle';
    }
  };

  return (
    <div className="maritime-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Voyage Details</h2>
          <p className="text-sm text-muted-foreground">
            Enter voyage information or auto-fetch from connected VMS
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleAutoFetch}
          iconName="RefreshCw"
          iconPosition="left"
        >
          Auto-Fetch from VMS
        </Button>
      </div>
      {/* System Status */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg">
        <h3 className="text-sm font-medium text-foreground mb-3">Connected Systems</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(connectedSystems)?.map(([system, data]) => (
            <div key={system} className="flex items-center space-x-2">
              <Icon 
                name={getSystemStatusIcon(data?.status)} 
                size={16} 
                className={getSystemStatusColor(data?.status)}
              />
              <span className="text-sm font-medium capitalize">{system}</span>
              <span className="text-xs text-muted-foreground">({data?.lastSync})</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vessel Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Vessel Information</h3>
          
          <Input
            label="Vessel Name"
            type="text"
            placeholder="Enter vessel name"
            value={formData?.vesselName}
            onChange={(e) => handleInputChange('vesselName', e?.target?.value)}
            required
          />

          <Select
            label="Vessel Type"
            placeholder="Select vessel type"
            options={vesselTypes}
            value={formData?.vesselType}
            onChange={(value) => handleInputChange('vesselType', value)}
            required
          />

          <Input
            label="Deadweight Tonnage (DWT)"
            type="number"
            placeholder="Enter DWT"
            value={formData?.dwt}
            onChange={(e) => handleInputChange('dwt', e?.target?.value)}
            required
          />

          <Input
            label="Voyage Number"
            type="text"
            placeholder="Enter voyage number"
            value={formData?.voyageNumber}
            onChange={(e) => handleInputChange('voyageNumber', e?.target?.value)}
            required
          />
        </div>

        {/* Voyage Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Voyage Information</h3>
          
          <Select
            label="Departure Port"
            placeholder="Select departure port"
            options={ports}
            value={formData?.departurePort}
            onChange={(value) => handleInputChange('departurePort', value)}
            searchable
            required
          />

          <Select
            label="Arrival Port"
            placeholder="Select arrival port"
            options={ports}
            value={formData?.arrivalPort}
            onChange={(value) => handleInputChange('arrivalPort', value)}
            searchable
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Distance (NM)"
              type="number"
              placeholder="Nautical miles"
              value={formData?.estimatedDistance}
              onChange={(e) => handleInputChange('estimatedDistance', e?.target?.value)}
            />

            <Input
              label="Duration (Days)"
              type="number"
              placeholder="Estimated days"
              value={formData?.estimatedDuration}
              onChange={(e) => handleInputChange('estimatedDuration', e?.target?.value)}
            />
          </div>
        </div>

        {/* Cargo Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Cargo Information</h3>
          
          <Select
            label="Cargo Type"
            placeholder="Select cargo type"
            options={cargoTypes}
            value={formData?.cargoType}
            onChange={(value) => handleInputChange('cargoType', value)}
            required
          />

          <Input
            label="Cargo Quantity (MT)"
            type="number"
            placeholder="Enter cargo quantity"
            value={formData?.cargoQuantity}
            onChange={(e) => handleInputChange('cargoQuantity', e?.target?.value)}
            required
          />
        </div>

        {/* Charter Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Charter Information</h3>
          
          <Select
            label="Charter Type"
            placeholder="Select charter type"
            options={charterTypes}
            value={formData?.charterType}
            onChange={(value) => handleInputChange('charterType', value)}
            required
          />

          <Select
            label="BIMCO ETS Clause"
            placeholder="Select BIMCO clause"
            options={bimcoClauses}
            value={formData?.bimcoClause}
            onChange={(value) => handleInputChange('bimcoClause', value)}
            description="Determines cost allocation methodology"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default VoyageDetailsForm;